<template>
  <!--
    AB#21901 — "was that the right call?"

    Shown once, a few seconds after an ELSA suggestion has been carried out
    (from the queue or the order page). One tap, optional, gone on its own if
    ignored. The answer is what teaches ELSA which suggestions to make less
    often; it never blocks anything.
  -->
  <Transition name="efp">
    <div
      v-if="visible && suggestion"
      class="efp"
      role="status"
      aria-live="polite"
      data-test="elsa-feedback-prompt"
    >
      <div class="efp-head">
        <AgentAvatar agent="elsa" :size="28" />
        <div class="efp-q">
          Was <b>{{ suggestion.label || 'that' }}</b> the right call?
          <span class="efp-sub"
            >One tap, optional. It teaches ELSA which suggestions to make less often.</span
          >
        </div>
        <button type="button" class="efp-x" aria-label="Not now" @click="close">×</button>
      </div>
      <div class="efp-btns">
        <button
          v-for="c in FEEDBACK_CHOICES"
          :key="c.value"
          type="button"
          class="efp-btn"
          :data-test="`elsa-feedback-${c.value}`"
          @click="answer(c.value)"
        >
          {{ c.label }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import {
  FEEDBACK_CHOICES,
  recordSuggestionFeedback,
  type OutcomeFeedback,
  type SuggestionRef,
} from '@/composables/useElsaOutcomes';

const props = withDefaults(
  defineProps<{
    /** The suggestion just carried out; a new value schedules a new prompt. */
    suggestion: SuggestionRef | null;
    /** Seconds after the action before the prompt appears. */
    delaySeconds?: number;
    /** Seconds the prompt stays before leaving on its own. */
    lingerSeconds?: number;
  }>(),
  { delaySeconds: 6, lingerSeconds: 45 }
);
const emit = defineEmits<{
  (e: 'answered', feedback: OutcomeFeedback): void;
  (e: 'closed'): void;
}>();

const visible = ref(false);
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

const clear = (): void => {
  if (showTimer) clearTimeout(showTimer);
  if (hideTimer) clearTimeout(hideTimer);
  showTimer = hideTimer = null;
};
const close = (): void => {
  clear();
  visible.value = false;
  emit('closed');
};
const answer = (feedback: OutcomeFeedback): void => {
  if (props.suggestion) recordSuggestionFeedback(props.suggestion, feedback);
  emit('answered', feedback);
  close();
};

watch(
  () => props.suggestion,
  (s) => {
    clear();
    visible.value = false;
    if (!s) return;
    showTimer = setTimeout(() => {
      visible.value = true;
      hideTimer = setTimeout(close, props.lingerSeconds * 1000);
    }, props.delaySeconds * 1000);
  },
  { immediate: true }
);
onBeforeUnmount(clear);
</script>

<style scoped>
.efp {
  position: absolute;
  right: 16px;
  bottom: 92px; /* clear of the Ask Atlas launcher */
  z-index: 30;
  width: min(100% - 32px, 400px);
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow-2, 0 12px 32px rgba(0, 0, 0, 0.28));
  font-size: 13px;
  color: var(--ink);
}
.efp-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.efp-q {
  flex: 1;
  line-height: 1.4;
}
.efp-q b {
  font-weight: 600;
}
.efp-sub {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--ink-mute);
}
.efp-x {
  border: 0;
  background: transparent;
  color: var(--ink-mute);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.efp-x:hover {
  color: var(--ink);
}
.efp-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.efp-btn {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface-2);
  color: var(--ink);
  padding: 6px 10px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}
.efp-btn:hover {
  border-color: var(--accent);
}
.efp-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.efp-enter-active,
.efp-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.efp-enter-from,
.efp-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
@media (prefers-reduced-motion: reduce) {
  .efp-enter-active,
  .efp-leave-active {
    transition: none;
  }
}
</style>
